import React, { useState, useEffect, useCallback } from 'react';
import { MdArrowBack, MdAdd, MdDelete, MdDragIndicator, MdExpandMore, MdExpandLess, MdSave, MdLink, MdLinkOff, MdDownload, MdOpenInNew } from 'react-icons/md';
import {
  getRubricsByAssignment,
  createRubric,
  updateRubric,
  getComponentQuestions
} from '../../utils/api/AssignmentsApi';

// ==================== Linked Question Chip ====================

const LinkedQuestionChip = ({ question, onUnlink }) => {
  const typeColors = {
    mcq: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    short_answer: 'bg-teal-50 text-teal-700 border-teal-200',
    short: 'bg-teal-50 text-teal-700 border-teal-200'
  };
  const colorClass = typeColors[question.question_type] || 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${colorClass}`}>
      <span className="font-medium">{question.question_id}</span>
      <span className="text-[10px] opacity-70">{question.question_type === 'mcq' ? 'MCQ' : 'Short'}</span>
      {onUnlink && (
        <button onClick={onUnlink} className="ml-0.5 hover:opacity-70">
          <MdLinkOff className="text-xs" />
        </button>
      )}
    </span>
  );
};

// ==================== Question Linker Modal ====================

const QuestionLinkerModal = ({ questions, linkedIds, onLink, onClose }) => {
  const unlinkedQuestions = questions.filter(q => !linkedIds.includes(q.question_id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-96 flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h4 className="text-sm font-bold text-gray-900 m-0">Link a Question</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {unlinkedQuestions.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">All questions are already linked.</div>
          ) : (
            unlinkedQuestions.map(q => (
              <button
                key={q.question_id}
                onClick={() => { onLink(q); onClose(); }}
                className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${q.question_type === 'mcq' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>
                    {q.question_type === 'mcq' ? 'MCQ' : 'SHORT'}
                  </span>
                  <span className="text-xs font-medium text-gray-700">{q.question_id}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1 line-clamp-2 m-0">{q.question_text}</p>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Rubric Item Card ====================

export const RubricItemCard = ({ item, onChange, onDelete, index, scoringMode, availableQuestions, onLinkQuestion, onUnlinkQuestion }) => {
  const [expanded, setExpanded] = useState(true);
  const [showLinker, setShowLinker] = useState(false);

  const handleFieldChange = (field, value) => {
    onChange({ ...item, [field]: value });
  };

  const handleTierChange = (tierIdx, field, value) => {
    const newTiers = [...(item.point_tiers || [])];
    newTiers[tierIdx] = { ...newTiers[tierIdx], [field]: value };
    handleFieldChange('point_tiers', newTiers);
  };

  const addTier = () => {
    const tiers = item.point_tiers || [];
    const nextPoints = tiers.length > 0
      ? Math.max(0, tiers[tiers.length - 1].points - Math.floor(item.max_points / 4))
      : item.max_points;
    handleFieldChange('point_tiers', [
      ...tiers,
      { points: Math.max(0, nextPoints), label: '', criteria: '' }
    ]);
  };

  const removeTier = (tierIdx) => {
    handleFieldChange('point_tiers', (item.point_tiers || []).filter((_, i) => i !== tierIdx));
  };

  const linkedQuestions = item.linked_questions || [];
  const linkedIds = linkedQuestions.map(lq => lq.question_id);
  const isNegative = scoringMode === 'negative';

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <MdDragIndicator className="text-gray-400 cursor-grab flex-shrink-0" />
        <span className="text-xs font-bold text-gray-400 w-6">#{index + 1}</span>
        <input
          type="text"
          value={item.title || ''}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="Item title..."
          className="flex-1 px-2 py-1 text-sm font-semibold border border-transparent rounded hover:border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent"
        />
        <div className="flex items-center gap-1">
          {isNegative && <span className="text-xs text-red-500 font-medium">-</span>}
          <input
            type="number"
            value={item.max_points || ''}
            onChange={(e) => handleFieldChange('max_points', parseFloat(e.target.value) || 0)}
            placeholder="0"
            className={`w-16 px-2 py-1 text-sm text-right border rounded focus:outline-none ${isNegative ? 'border-red-300 focus:border-red-500 text-red-700' : 'border-gray-300 focus:border-blue-500'}`}
            min="0"
            step="1"
          />
          <span className="text-xs text-gray-500">pts</span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
        >
          {expanded ? <MdExpandLess /> : <MdExpandMore />}
        </button>
        <button
          onClick={onDelete}
          className="p-1 text-gray-400 hover:text-red-500 rounded"
        >
          <MdDelete />
        </button>
      </div>

      {/* Body */}
      {expanded && (
        <div className="p-4 space-y-3">
          {/* Category & Grading Type */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <input
                type="text"
                value={item.category || ''}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                placeholder="e.g., entities, relationships, general"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="w-48">
              <label className="block text-xs font-medium text-gray-600 mb-1">Grading Type</label>
              <select
                value={item.grading_type || 'all_or_nothing'}
                onChange={(e) => handleFieldChange('grading_type', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
              >
                <option value="all_or_nothing">All or Nothing</option>
                <option value="tiered">Tiered</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={item.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this rubric item evaluates..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none min-h-[60px] resize-none"
            />
          </div>

          {/* Point Tiers (if tiered) */}
          {item.grading_type === 'tiered' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Point Tiers</label>
              <div className="space-y-2">
                {(item.point_tiers || []).map((tier, tIdx) => (
                  <div key={tIdx} className="flex items-center gap-2 pl-2 border-l-2 border-blue-200">
                    <input
                      type="number"
                      value={tier.points || ''}
                      onChange={(e) => handleTierChange(tIdx, 'points', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none text-right"
                      min="0"
                      max={item.max_points}
                    />
                    <span className="text-xs text-gray-400">pts</span>
                    <input
                      type="text"
                      value={tier.label || ''}
                      onChange={(e) => handleTierChange(tIdx, 'label', e.target.value)}
                      placeholder="Label (e.g., Excellent)"
                      className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={tier.criteria || ''}
                      onChange={(e) => handleTierChange(tIdx, 'criteria', e.target.value)}
                      placeholder="Criteria description..."
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                    />
                    <button
                      onClick={() => removeTier(tIdx)}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <MdDelete className="text-sm" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addTier}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                >
                  <MdAdd className="text-sm" /> Add Tier
                </button>
              </div>
            </div>
          )}

          {/* Linked Questions */}
          {availableQuestions && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Linked Questions</label>
              <div className="flex flex-wrap items-center gap-1.5">
                {linkedQuestions.map(lq => (
                  <LinkedQuestionChip
                    key={lq.question_id}
                    question={lq}
                    onUnlink={() => onUnlinkQuestion && onUnlinkQuestion(item.item_id, lq.question_id)}
                  />
                ))}
                <button
                  onClick={() => setShowLinker(true)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-blue-600 border border-dashed border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <MdLink className="text-xs" /> Link
                </button>
              </div>
              {linkedQuestions.length > 0 && (
                <p className="text-[10px] text-gray-400 mt-1 m-0">
                  {linkedQuestions.filter(lq => lq.auto_grade).length} auto-graded
                </p>
              )}
            </div>
          )}

          {/* Example Feedback */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Example Feedback (optional)</label>
            <textarea
              value={item.example_feedback || ''}
              onChange={(e) => handleFieldChange('example_feedback', e.target.value)}
              placeholder="Example feedback to give students..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:outline-none min-h-[40px] resize-none"
            />
          </div>
        </div>
      )}

      {/* Question Linker Modal */}
      {showLinker && availableQuestions && (
        <QuestionLinkerModal
          questions={availableQuestions}
          linkedIds={linkedIds}
          onLink={(q) => onLinkQuestion && onLinkQuestion(item.item_id, q)}
          onClose={() => setShowLinker(false)}
        />
      )}
    </div>
  );
};

// ==================== Rubric Section ====================

const RubricSection = ({ title, subtitle, rubric, onChange, componentPoints, scoringMode, availableQuestions, onImportQuestions }) => {
  const items = rubric.items || [];

  const handleItemChange = (idx, updatedItem) => {
    const newItems = [...items];
    newItems[idx] = updatedItem;
    onChange({ ...rubric, items: newItems });
  };

  const handleItemDelete = (idx) => {
    onChange({ ...rubric, items: items.filter((_, i) => i !== idx) });
  };

  const addItem = () => {
    const newItem = {
      item_id: `item_${Date.now()}`,
      order: items.length,
      category: 'general',
      title: '',
      description: '',
      max_points: 0,
      grading_type: 'all_or_nothing',
      point_tiers: [],
      example_feedback: '',
      linked_questions: [],
      is_active: true
    };
    onChange({ ...rubric, items: [...items, newItem] });
  };

  const handleLinkQuestion = (itemId, question) => {
    const newItems = items.map(item => {
      if (item.item_id !== itemId) return item;
      const existing = item.linked_questions || [];
      if (existing.some(lq => lq.question_id === question.question_id)) return item;
      return {
        ...item,
        linked_questions: [...existing, {
          question_id: question.question_id,
          question_type: question.question_type,
          source: question.source,
          source_id: question.source_id,
          auto_grade: question.question_type === 'mcq'
        }]
      };
    });
    onChange({ ...rubric, items: newItems });
  };

  const handleUnlinkQuestion = (itemId, questionId) => {
    const newItems = items.map(item => {
      if (item.item_id !== itemId) return item;
      return {
        ...item,
        linked_questions: (item.linked_questions || []).filter(lq => lq.question_id !== questionId)
      };
    });
    onChange({ ...rubric, items: newItems });
  };

  const totalPoints = items.reduce((sum, item) => sum + (item.max_points || 0), 0);

  // Get all linked question IDs across all items
  const allLinkedIds = items.flatMap(item => (item.linked_questions || []).map(lq => lq.question_id));
  const unlinkedQuestions = (availableQuestions || []).filter(q => !allLinkedIds.includes(q.question_id));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Section Header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 m-0">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 m-0 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {onImportQuestions && unlinkedQuestions.length > 0 && (
              <button
                onClick={onImportQuestions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <MdDownload className="text-sm" /> Import {unlinkedQuestions.length} Questions
              </button>
            )}
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-900">{totalPoints} pts</div>
              {componentPoints !== undefined && (
                <div className={`text-xs ${totalPoints !== componentPoints ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                  / {componentPoints} pts allocated
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Rubric title input */}
        <input
          type="text"
          value={rubric.title || ''}
          onChange={(e) => onChange({ ...rubric, title: e.target.value })}
          placeholder="Rubric title..."
          className="mt-2 w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Items */}
      <div className="p-4 space-y-3">
        {items.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            No rubric items yet. Click "Add Item" to start building.
          </div>
        )}
        {items.map((item, idx) => (
          <RubricItemCard
            key={item.item_id || idx}
            item={item}
            index={idx}
            onChange={(updated) => handleItemChange(idx, updated)}
            onDelete={() => handleItemDelete(idx)}
            scoringMode={scoringMode}
            availableQuestions={availableQuestions}
            onLinkQuestion={handleLinkQuestion}
            onUnlinkQuestion={handleUnlinkQuestion}
          />
        ))}
        <button
          onClick={addItem}
          className="flex items-center gap-2 w-full justify-center px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          <MdAdd /> Add Rubric Item
        </button>
      </div>
    </div>
  );
};

// ==================== Summary Panel ====================

const SummaryPanel = ({ rubrics, components, assignment, scoringMode, onScoringModeChange, activeTab, onNavigate }) => {
  const allRubrics = Object.entries(rubrics);

  const grandTotal = allRubrics.reduce((sum, [, rubric]) =>
    sum + (rubric.items || []).reduce((s, i) => s + (i.max_points || 0), 0), 0
  );

  const totalLinkedQuestions = allRubrics.reduce((sum, [, rubric]) =>
    sum + (rubric.items || []).reduce((s, i) => s + (i.linked_questions || []).length, 0), 0
  );

  const activeComponent = activeTab !== 'assignment' ? components.find(c => c.component_id === activeTab) : null;

  const handleOpenComponent = () => {
    if (!onNavigate || !activeComponent || !assignment) return;
    const navData = {
      assignmentId: assignment.assignment_id,
      componentId: activeComponent.component_id
    };
    switch (activeComponent.type) {
      case 'document':
        navData.documentId = activeComponent.content_ref?.document_id;
        onNavigate('document-editor', navData);
        break;
      case 'erd':
        navData.environmentId = activeComponent.content_ref?.requirement_id;
        onNavigate('editor', navData);
        break;
      case 'code':
        navData.projectId = activeComponent.content_ref?.project_id;
        navData.fileId = activeComponent.content_ref?.file_id;
        onNavigate('code-editor', navData);
        break;
      default:
        onNavigate('document-editor', navData);
    }
  };

  return (
    <div className="space-y-4">
      {/* Open in Editor */}
      {activeComponent && onNavigate && (
        <button
          onClick={handleOpenComponent}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <MdOpenInNew className="text-base" />
          Open in {activeComponent.type === 'erd' ? 'ERD' : activeComponent.type === 'code' ? 'Code' : 'Document'} Editor
        </button>
      )}

      {/* Scoring Mode */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h4 className="text-sm font-bold text-gray-900 m-0 mb-3">Scoring Mode</h4>
        <div className="flex gap-2">
          <button
            onClick={() => onScoringModeChange('positive')}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
              scoringMode === 'positive'
                ? 'bg-green-50 border-green-300 text-green-800'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <div className="font-bold">+ Positive</div>
            <div className="mt-0.5 opacity-70">Add points from 0</div>
          </button>
          <button
            onClick={() => onScoringModeChange('negative')}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
              scoringMode === 'negative'
                ? 'bg-red-50 border-red-300 text-red-800'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <div className="font-bold">- Negative</div>
            <div className="mt-0.5 opacity-70">Deduct from max</div>
          </button>
        </div>
      </div>

      {/* Points Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h4 className="text-sm font-bold text-gray-900 m-0 mb-3">Points Summary</h4>
        <div className="space-y-2">
          {allRubrics.map(([key, rubric]) => {
            const itemCount = (rubric.items || []).length;
            const pts = (rubric.items || []).reduce((s, i) => s + (i.max_points || 0), 0);
            const label = key === 'assignment'
              ? 'Overall'
              : (components.find(c => c.component_id === key)?.title || key);
            const comp = components.find(c => c.component_id === key);
            const allocated = key === 'assignment' ? assignment?.total_points : comp?.points;

            return (
              <div key={key} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                <div>
                  <div className="text-xs font-medium text-gray-800">{label}</div>
                  <div className="text-[10px] text-gray-400">{itemCount} items</div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${allocated !== undefined && pts !== allocated ? 'text-amber-600' : 'text-gray-900'}`}>
                    {pts} pts
                  </div>
                  {allocated !== undefined && (
                    <div className="text-[10px] text-gray-400">/ {allocated}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
          <span className="text-sm font-bold text-gray-900">Total</span>
          <span className={`text-lg font-bold ${grandTotal !== (assignment?.total_points || 0) ? 'text-amber-600' : 'text-green-700'}`}>
            {grandTotal} pts
          </span>
        </div>
      </div>

      {/* Linked Questions */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h4 className="text-sm font-bold text-gray-900 m-0 mb-1">Linked Questions</h4>
        <p className="text-xs text-gray-500 m-0">{totalLinkedQuestions} questions linked to rubric items</p>
        {totalLinkedQuestions > 0 && (
          <p className="text-[10px] text-gray-400 m-0 mt-1">MCQ questions will be auto-graded when submissions are scored.</p>
        )}
      </div>
    </div>
  );
};

// ==================== Main RubricBuilder ====================

const RubricBuilder = ({ assignment, classContext, onBack, onNavigate }) => {
  const [rubrics, setRubrics] = useState({}); // keyed by component_id (null key = 'assignment')
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('assignment'); // 'assignment' or component_id
  const [scoringMode, setScoringMode] = useState('positive');
  const [componentQuestions, setComponentQuestions] = useState({}); // keyed by component_id

  const components = assignment?.components || [];

  // Load existing rubrics
  useEffect(() => {
    if (!assignment?.assignment_id) return;

    const loadRubrics = async () => {
      try {
        setLoading(true);
        const data = await getRubricsByAssignment(assignment.assignment_id, classContext);
        const rubricList = data.rubrics || [];

        // Group by component_id
        const grouped = {};

        // Initialize assignment-level rubric
        const assignmentRubric = rubricList.find(r => !r.component_id);
        grouped['assignment'] = assignmentRubric || {
          title: `${assignment.title} - Overall Rubric`,
          items: [],
          _isNew: true
        };

        // Pick up scoring mode from first rubric that has one
        const firstWithMode = rubricList.find(r => r.scoring_mode);
        if (firstWithMode) {
          setScoringMode(firstWithMode.scoring_mode);
        }

        // Initialize per-component rubrics
        for (const comp of components) {
          const compRubric = rubricList.find(r => r.component_id === comp.component_id);
          grouped[comp.component_id] = compRubric || {
            title: `${comp.title} Rubric`,
            items: [],
            component_id: comp.component_id,
            _isNew: true
          };
        }

        setRubrics(grouped);
      } catch (err) {
        console.error('Error loading rubrics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadRubrics();
  }, [assignment?.assignment_id]);

  // Load component questions for linking
  useEffect(() => {
    if (!assignment?.assignment_id || !classContext) return;

    const loadQuestions = async () => {
      const questionsMap = {};
      for (const comp of components) {
        try {
          const data = await getComponentQuestions(assignment.assignment_id, comp.component_id, classContext);
          questionsMap[comp.component_id] = data.questions || [];
        } catch {
          questionsMap[comp.component_id] = [];
        }
      }
      setComponentQuestions(questionsMap);
    };

    loadQuestions();
  }, [assignment?.assignment_id, components.length]);

  const handleRubricChange = useCallback((key, updatedRubric) => {
    setRubrics(prev => ({ ...prev, [key]: updatedRubric }));
  }, []);

  const handleImportQuestions = (tabKey) => {
    const questions = componentQuestions[tabKey] || [];
    if (questions.length === 0) return;

    const currentRubric = rubrics[tabKey] || { items: [] };
    const existingLinkedIds = (currentRubric.items || []).flatMap(
      item => (item.linked_questions || []).map(lq => lq.question_id)
    );

    const unlinked = questions.filter(q => !existingLinkedIds.includes(q.question_id));
    if (unlinked.length === 0) return;

    const newItems = unlinked.map((q, idx) => ({
      item_id: `item_${Date.now()}_${idx}`,
      order: (currentRubric.items || []).length + idx,
      category: 'question',
      title: q.question_text.length > 80 ? q.question_text.substring(0, 80) + '...' : q.question_text,
      description: q.question_text,
      max_points: q.question_type === 'mcq' ? 1 : 5,
      grading_type: 'all_or_nothing',
      point_tiers: [],
      example_feedback: '',
      linked_questions: [{
        question_id: q.question_id,
        question_type: q.question_type,
        source: q.source,
        source_id: q.source_id,
        auto_grade: q.question_type === 'mcq'
      }],
      is_active: true
    }));

    handleRubricChange(tabKey, {
      ...currentRubric,
      items: [...(currentRubric.items || []), ...newItems]
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);

      for (const [key, rubric] of Object.entries(rubrics)) {
        const componentId = key === 'assignment' ? null : key;

        const rubricData = {
          assignment_id: assignment.assignment_id,
          title: rubric.title || 'Untitled Rubric',
          description: rubric.description || '',
          scoring_mode: scoringMode,
          items: (rubric.items || []).map((item, idx) => ({
            ...item,
            order: idx
          })),
          component_id: componentId
        };

        if (rubric.rubric_id && !rubric._isNew) {
          // Update existing
          await updateRubric(rubric.rubric_id, rubricData, classContext);
        } else if (rubric.items && rubric.items.length > 0) {
          // Create new (only if it has items)
          const result = await createRubric(rubricData, classContext);
          if (result.success && result.rubric_id) {
            setRubrics(prev => ({
              ...prev,
              [key]: { ...prev[key], rubric_id: result.rubric_id, _isNew: false }
            }));
          }
        }
      }

      setSaveMessage('Rubrics saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Error saving rubrics:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Build tab list
  const tabs = [
    { key: 'assignment', label: 'Overall', icon: null }
  ];
  for (const comp of components) {
    const typeColors = {
      document: 'bg-blue-100 text-blue-700',
      erd: 'bg-purple-100 text-purple-700',
      code: 'bg-green-100 text-green-700'
    };
    tabs.push({
      key: comp.component_id,
      label: comp.title,
      type: comp.type,
      points: comp.points,
      colorClass: typeColors[comp.type] || 'bg-gray-100 text-gray-700'
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading rubrics...</div>
      </div>
    );
  }

  const activeRubric = rubrics[activeTab] || { title: '', items: [] };
  const activeComponent = components.find(c => c.component_id === activeTab);
  const activeQuestions = activeTab !== 'assignment' ? (componentQuestions[activeTab] || null) : null;

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <MdArrowBack className="text-xl" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 m-0">Rubric Builder</h1>
            <p className="text-sm text-gray-500 m-0">{assignment?.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saveMessage && (
            <span className="text-sm text-green-600 font-medium">{saveMessage}</span>
          )}
          {error && (
            <span className="text-sm text-red-600">{error}</span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
          >
            <MdSave />
            {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.type && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${tab.colorClass}`}>
                    {tab.type === 'document' ? 'DOC' : tab.type === 'erd' ? 'ERD' : 'CODE'}
                  </span>
                )}
                {tab.label}
                {tab.points !== undefined && (
                  <span className="text-xs text-gray-400">{tab.points}pts</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content - Two Column Layout */}
      <div className="flex-1 min-h-0 flex">
        {/* Left Column - Rubric Editor */}
        <div className="flex-1 overflow-y-auto p-6">
          <RubricSection
            key={activeTab}
            title={activeTab === 'assignment' ? 'Assignment-Level Rubric' : `${activeComponent?.title || 'Component'} Rubric`}
            subtitle={activeTab === 'assignment'
              ? 'Criteria that apply to the entire assignment'
              : `Criteria specific to this ${activeComponent?.type || 'component'} component`
            }
            rubric={activeRubric}
            onChange={(updated) => handleRubricChange(activeTab, updated)}
            componentPoints={activeTab === 'assignment' ? assignment?.total_points : activeComponent?.points}
            scoringMode={scoringMode}
            availableQuestions={activeQuestions}
            onImportQuestions={activeQuestions && activeQuestions.length > 0 ? () => handleImportQuestions(activeTab) : null}
          />
        </div>

        {/* Right Column - Summary Panel */}
        <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto p-4 flex-shrink-0">
          <SummaryPanel
            rubrics={rubrics}
            components={components}
            assignment={assignment}
            scoringMode={scoringMode}
            onScoringModeChange={setScoringMode}
            activeTab={activeTab}
            onNavigate={onNavigate}
          />
        </div>
      </div>
    </div>
  );
};

export default RubricBuilder;
